import { cn } from "@/lib/utils"
import { Monitor, Clock, Newspaper, Trash2, Youtube, CloudSun, RotateCcw, Loader2, CheckCircle, XCircle } from "lucide-react"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"

import { motion } from "framer-motion"
import { useState, useRef, useEffect } from "react"

interface Device {
  id: string;
  code: string;
  name: string;
  status: 'active' | 'inactive';
  screen_id?: string;
  activated_at: string;
  user_id: string;
  weather_config?: {
    enabled: boolean;
    interval: number;
    duration: number; // segundos
  };
}

interface Screen {
  id: string;
  name: string;
  title?: string;
}

interface DeviceCardContentProps {
  isExpanded: boolean;
  device: Device;
  screens: Screen[];
  settings: any;
  loading: boolean;
  isDeleting: boolean;
  onScreenChange: (screenId: string) => void;
  onToggleClock: (enabled: boolean) => void;
  onToggleNewsTicker: (enabled: boolean) => void;
  onToggleYoutube?: (enabled: boolean) => void;
  onUpdateWeatherConfig: (config: { enabled: boolean; interval: number; duration: number }) => void;
  onDelete: () => void;
  isUpdating: boolean;
  onUpdateSettings?: (settings: any) => void;
}

export function DeviceCardContent({
  isExpanded,
  device,
  screens,
  settings,
  loading,
  isDeleting,
  onScreenChange,
  onToggleClock,
  onToggleNewsTicker,
  onToggleYoutube,
  onUpdateWeatherConfig,
  onDelete,
  isUpdating,
  onUpdateSettings
}: DeviceCardContentProps) {
  const [showStreamModal, setShowStreamModal] = useState(false);

  // Estados para Reset Storage remoto
  const [isResetting, setIsResetting] = useState(false);
  const [resetStatus, setResetStatus] = useState<'success' | 'error' | null>(null);
  const { toast } = useToast();

  // Estado local para weather config
  const [weatherConfig, setWeatherConfig] = useState(device.weather_config || {
    enabled: false,
    interval: 15,
    duration: 20
  });

  // Ref para debounce do save automático
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleWeatherToggle = (enabled: boolean) => {
    const newConfig = { ...weatherConfig, enabled };
    setWeatherConfig(newConfig);
    onUpdateWeatherConfig(newConfig);
  };

  const handleWeatherChange = (field: 'interval' | 'duration', value: string) => {
    let numValue = parseInt(value);
    if (isNaN(numValue)) numValue = 0;

    // Validação imediata para duration visualmente
    if (field === 'duration' && numValue > 20) numValue = 20;
    if (field === 'interval' && numValue < 1) numValue = 1;

    const newConfig = { ...weatherConfig, [field]: numValue };
    setWeatherConfig(newConfig);

    // Debounce: salvar automaticamente após 500ms (funciona com setas e digitação)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      onUpdateWeatherConfig(newConfig);
    }, 500);
  };

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const saveWeatherConfig = () => {
    // Cancelar debounce pendente e salvar imediatamente
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    onUpdateWeatherConfig(weatherConfig);
  };

  // Função para salvar a streamUrl no banco (via onUpdateSettings se existir)
  const onSaveStreamUrl = async (url: string) => {
    if (typeof onToggleYoutube === 'function' && typeof onDelete !== 'undefined') {
      // Se existir função de updateSettings, use-a
      if (typeof onUpdateSettings === 'function') {
        await onUpdateSettings({ streamUrl: url });
      }
    }
  };

  // Handler para enviar comando de Reset Storage remoto
  const handleResetStorage = async () => {
    setIsResetting(true);
    setResetStatus(null);

    try {
      // Criar expiração de 5 minutos
      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + 5);

      // Inserir comando na tabela device_commands
      const { error: commandError } = await supabase
        .from('device_commands')
        .insert([
          {
            device_id: device.id,
            type: 'WIPE_DATA',
            status: 'PENDING',
            expires_at: expiresAt.toISOString(),
            metadata: {
              device_name: device.name,
              device_code: device.code,
              initiated_from: 'dashboard'
            }
          }
        ]);

      if (commandError) {
        console.error('Erro ao enviar comando de reset:', commandError);
        throw commandError;
      }

      setResetStatus('success');
      toast({
        title: "Comando enviado",
        description: `O comando de reset foi enviado para "${device.name}". O dispositivo será resetado em instantes.`,
      });

      // Limpar status após 3 segundos
      setTimeout(() => setResetStatus(null), 3000);

    } catch (error) {
      console.error('Erro ao enviar comando de reset:', error);
      setResetStatus('error');
      toast({
        title: "Erro ao enviar comando",
        description: "Não foi possível enviar o comando de reset. Verifique se a tabela device_commands existe no banco.",
        variant: "destructive"
      });
      setTimeout(() => setResetStatus(null), 3000);
    } finally {
      setIsResetting(false);
    }
  };

  if (!isExpanded) return null;

  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="overflow-hidden"
    >
      <div className="py-4 space-y-4 border-t mt-4" onClick={e => e.stopPropagation()}>
        <div>
          <label className="text-sm font-medium mb-2 block">Tela Associada</label>
          <Select
            value={device.screen_id || ""}
            onValueChange={onScreenChange}
            disabled={isUpdating}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione uma tela" />
            </SelectTrigger>
            <SelectContent>
              {screens.map((screen) => (
                <SelectItem key={screen.id} value={screen.id}>
                  <div className="flex items-center space-x-2">
                    <Monitor className="h-4 w-4" />
                    <span>{screen.name}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          {/* Configuração de Clima */}
          <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
            <div className="flex items-center justify-between p-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-full dark:bg-blue-900/30">
                  <CloudSun className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-sm">Previsão do Tempo</span>
                  <span className="text-xs text-muted-foreground">Exibir previsão local sobre o player</span>
                </div>
              </div>
              <Switch
                checked={weatherConfig.enabled}
                onCheckedChange={handleWeatherToggle}
                disabled={isUpdating}
              />
            </div>

            {weatherConfig.enabled && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                className="grid grid-cols-2 gap-4 p-3 pt-0 border-t bg-muted/20"
              >
                <div className="space-y-1.5 mt-3">
                  <Label className="text-xs text-muted-foreground">Intervalo (minutos)</Label>
                  <Input
                    type="number"
                    min={1}
                    value={weatherConfig.interval}
                    onChange={(e) => handleWeatherChange('interval', e.target.value)}
                    onBlur={saveWeatherConfig}
                    className="h-8 bg-background"
                    disabled={isUpdating}
                  />
                </div>
                <div className="space-y-1.5 mt-3">
                  <Label className="text-xs text-muted-foreground">Duração (segundos)</Label>
                  <Input
                    type="number"
                    min={5}
                    max={20}
                    value={weatherConfig.duration}
                    onChange={(e) => handleWeatherChange('duration', e.target.value)}
                    onBlur={saveWeatherConfig}
                    className="h-8 bg-background"
                    disabled={isUpdating}
                  />
                </div>
              </motion.div>
            )}
          </div>
        </div>

        <div className="pt-2 flex justify-end gap-2">
          {/* Botão Resetar Storage */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={device.status !== 'active' || isResetting}
                className={cn(
                  "transition-all",
                  resetStatus === 'success' && "border-green-500 text-green-600",
                  resetStatus === 'error' && "border-red-500 text-red-600"
                )}
                title={device.status !== 'active' ? 'Dispositivo offline' : 'Resetar storage remotamente'}
              >
                {isResetting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : resetStatus === 'success' ? (
                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                ) : resetStatus === 'error' ? (
                  <XCircle className="h-4 w-4 mr-2 text-red-600" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                {isResetting ? 'Enviando...' : resetStatus === 'success' ? 'Enviado!' : resetStatus === 'error' ? 'Erro' : 'Resetar Storage'}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar Reset Remoto</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <span className="block">
                      Tem certeza que deseja resetar o storage do dispositivo <strong>"{device.name}"</strong>?
                    </span>
                    <span className="block text-amber-600 dark:text-amber-400 text-sm">
                      Isso irá apagar todos os vídeos, imagens e configurações armazenadas localmente.
                      O dispositivo irá reiniciar automaticamente.
                    </span>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleResetStorage}
                  disabled={isResetting}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {isResetting ? "Enviando..." : "Resetar Agora"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Botão Remover */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Trash2 className="h-4 w-4 mr-2" />
                Remover
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Confirmar remoção</AlertDialogTitle>
                <AlertDialogDescription>
                  Tem certeza que deseja remover o dispositivo "{device.name}"? Esta ação não pode ser desfeita.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction onClick={() => onDelete()} disabled={isDeleting}>
                  {isDeleting ? "Removendo..." : "Remover"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </motion.div>
  );
}
